import React from "react";
import { useQuery, gql } from "@apollo/client";
import { Link } from "react-router-dom";
import { isoDateToMDY } from "../../../utils/common";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Edit, AlertTriangle, Search, Info } from "lucide-react";
import { TabHeader } from "../shared/components/TabHeader";
import { TabSpinner } from "../shared/components/TabSpinner";
import { TabToolbar } from "../shared/components/TabToolbar";
import { Input } from "@/components/ui/input";
import { TabTable } from "../shared/components/TabTable";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/ui/EmptyState";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const GET_INCIDENTS_DATA = gql`
  query GetIncidentsData($programNumber: Int!) {
    getParticipantByODRNumber(odrnumber: $programNumber) {
      incidents {
        id
        incidentClientsCid
        incidentClientsNames
        incidentDate
        incidentLocationName
        incidentNumber
        incidentTime
        loginName
        organizationName
        primaryContactName
        program
        reportCategory
      }
    }
  }
`;

interface Incident {
  id: string;
  incidentClientsCid: string;
  incidentClientsNames: string;
  incidentDate: string;
  incidentLocationName: string;
  incidentNumber: string;
  incidentTime: string;
  loginName: string;
  organizationName: string;
  primaryContactName: string;
  program: string;
  reportCategory: string;
}

interface IncidentsProps {
  programNumber: number;
}

const ErrorState = ({ error }: { error: Error }) => (
  <div className="p-12">
    <EmptyState
      icon={AlertTriangle}
      title="Error loading incidents"
      description={error.message}
    />
  </div>
);

export const Incidents: React.FC<IncidentsProps> = ({ programNumber }) => {
  const { loading, error, data } = useQuery(GET_INCIDENTS_DATA, {
    variables: { programNumber },
    skip: !programNumber,
  });
  const [search, setSearch] = React.useState("");

  if (loading) return <TabSpinner label="Loading incidents..." />;
  if (error) return <ErrorState error={error} />;
  if (!data) return null;

  const incidentsData: Incident[] = data.getParticipantByODRNumber.incidents || [];
  const filteredIncidents = incidentsData.filter((incident) => {
    const term = search.toLowerCase();
    return (
      incident.reportCategory?.toLowerCase().includes(term) ||
      incident.program?.toLowerCase().includes(term) ||
      incident.incidentLocationName?.toLowerCase().includes(term) ||
      incident.loginName?.toLowerCase().includes(term)
    );
  });

  const renderCategory = (category: string | null | undefined) => {
    if (!category) return "—";
    return (
      <Badge variant="outline" className="text-xs font-bold uppercase tracking-wider bg-amber-50 text-amber-700 border-amber-100 shadow-sm">
        {category}
      </Badge>
    );
  };

  const renderProgram = (program: string | null | undefined) => {
    if (!program) return "—";
    return (
      <Badge variant="outline" className="text-xs font-bold uppercase tracking-wider bg-blue-50 text-blue-700 border-blue-100 shadow-sm">
        {program}
      </Badge>
    );
  };

  return (
    <div className="space-y-4 h-full flex flex-col">
      <TabHeader
        icon={AlertTriangle}
        title="Incidents"
        subtitle="Incident reports linked to the participant"
      />
      <TabToolbar
        left={
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search incidents..."
              className="h-8 w-64 pl-8 text-xs bg-gray-50/50 border-gray-200 focus-visible:ring-1"
            />
          </div>
        }
      />
      <div className="flex-1 min-h-0 flex flex-col">
        <Card className="flex-1 overflow-hidden rounded-none border-0 shadow-none bg-white">
          <CardContent className="p-0 h-full flex flex-col">
            <div className="border-t border-b border-gray-200 flex-1 overflow-auto">
              <TabTable<Incident>
                data={filteredIncidents}
                emptyMessage={search ? "No matching incidents found." : "No incidents recorded for this participant."}
                columns={[
                  { title: "Date", className: "min-w-[110px]", render: (i) => (
                    <span className="text-xs font-medium text-gray-900">{isoDateToMDY(i.incidentDate) || "—"}</span>
                  )},
                  { title: "Time", className: "min-w-[90px]", render: (i) => <span className="text-xs text-gray-600">{i.incidentTime || "—"}</span> },
                  { title: "Program", className: "min-w-[130px]", render: (i) => renderProgram(i.program) },
                  { title: "Category", className: "min-w-[160px]", render: (i) => renderCategory(i.reportCategory) },
                  { title: "Location", className: "min-w-[150px]", render: (i) => <span className="text-xs text-gray-700">{i.incidentLocationName || "—"}</span> },
                  { title: "Reported by", className: "min-w-[120px]", render: (i) => <span className="text-xs text-gray-600 italic">{i.loginName || "—"}</span> },
                  { title: "Actions", className: "w-[80px] text-right", render: (i) => (
                    <div className="flex justify-end">
                      <Link to={`/candidates/inrep?searchField=id&search=${i.id}`}>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400 hover:text-blue-600 hover:bg-blue-50" title="View Full Report">
                          <Info className="h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  )},
                ]}
              />
              {filteredIncidents.length === 0 && (
                <div className="py-20">
                  <EmptyState
                    icon={AlertTriangle}
                    title={search ? "No matching incidents" : "No incidents found"}
                    description={search ? `No results for "${search}"` : "This participant has no linked incident reports."}
                    action={search ? <Button variant="outline" size="sm" onClick={() => setSearch("")}>Clear Search</Button> : undefined}
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
